import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot, type QueryConstraint, type DocumentData } from "firebase/firestore";
import { db, call } from "../firebase";
import { useSession } from "../session";
import { SITES, DOC_CATEGORIES, canSelfApprove, removeDiacritics, type Member } from "../domain";
import { friendly, toast } from "../errors";
import { ask } from "../dialog";

type Row = DocumentData & { id: string };

/** Hàng chờ duyệt mà người này có quyền duyệt. Quản lý cơ sở: chỉ cơ sở mình (tài liệu Toàn công ty do BGĐ/Quản lý chung duyệt). */
export function reviewQuery(m: Member) {
  const c: QueryConstraint[] = [where("status", "==", "pending_review")];
  if (m.role === "site_mgr") c.push(where("siteId", "in", m.sites.length ? m.sites : ["-"]));
  return query(collection(db, "documents"), ...c);
}

export default function Review() {
  const { member } = useSession();
  const nav = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [rejecting, setRejecting] = useState<Row | null>(null);
  const [note, setNote] = useState("");
  const [noteErr, setNoteErr] = useState(false);
  // L41: tìm và lọc như Kho
  const [q, setQ] = useState("");
  const [site, setSite] = useState("");
  const [cat, setCat] = useState("");
  const [who, setWho] = useState("");

  const scopeKey = member ? `${member.role}|${member.sites.join(",")}` : "";
  useEffect(() => {
    if (!member) return;
    return onSnapshot(reviewQuery(member), (s) => {
      const list: Row[] = s.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.updatedAt?.toMillis?.() ?? 0) - (b.updatedAt?.toMillis?.() ?? 0)); // cũ nhất lên trước
      setRows(list);
      setPicked((p) => new Set([...p].filter((id) => list.some((r) => r.id === id))));
    }, (e) => setErr(friendly(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const shown = useMemo(() => {
    const k = removeDiacritics(q).toLowerCase().trim();
    return rows.filter((r) => (!site || r.siteId === site) && (!cat || r.category === cat) && (!who || (r.pendingByName ?? r.createdByName) === who)
      && (!k || removeDiacritics(`${r.code} ${r.title} ${r.subcategory} ${r.pendingByName ?? ""}`).toLowerCase().includes(k)));
  }, [rows, q, site, cat, who]);

  if (!member) return null;
  // Q12: Pháp lý / Hợp đồng / Tài chính / Nhân sự do chính mình tải lên → cần người khác duyệt
  const selfBlocked = (r: Row) => r.pendingBy === member.email && !canSelfApprove(member, r.category);
  const approvable = shown.filter((r) => !selfBlocked(r));
  const uploaders = [...new Set(rows.map((r) => (r.pendingByName ?? r.createdByName) as string).filter(Boolean))].sort((a, b) => a.localeCompare(b, "vi"));
  const siteOpts = [...new Set(rows.map((r) => r.siteId as string))];
  const catOpts = [...new Set(rows.map((r) => r.category as string))].sort();

  const open = (r: Row) => nav(`/xem/${r.id}?v=${r.pendingVersion}`);
  const toggle = (id: string) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const approve = async (list: Row[], confirmFirst = false) => {
    // L40: duyệt hàng loạt luôn hỏi lại, liệt kê đúng các mục sẽ duyệt
    if (confirmFirst && !await ask({ title: `Duyệt ${list.length} tài liệu đã chọn?`, confirm: `Duyệt ${list.length} mục`,
      message: <ul style={{ margin: "4px 0 0", paddingLeft: 18, maxHeight: 220, overflow: "auto" }}>{list.map((r) => <li key={r.id}><b>{r.code}</b> · {r.title}</li>)}</ul> })) return;
    setBusy(true); setErr(null);
    let ok = 0;
    for (const r of list) {
      try { await call("reviewDocument")({ docId: r.id, decision: "approve" }); ok++; }
      catch (e) { toast(`${r.code}: ${friendly(e)}`); }
    }
    if (ok) toast(ok === 1 ? `Đã duyệt ${list[0].code}.` : `Đã duyệt ${ok} tài liệu.`, "ok");
    setPicked(new Set());
    setBusy(false);
  };

  const reject = async () => {
    if (!rejecting) return;
    if (!note.trim()) { setNoteErr(true); return; }
    setBusy(true);
    try {
      await call("reviewDocument")({ docId: rejecting.id, decision: "reject", note: note.trim() });
      toast(`Đã trả lại ${rejecting.code}.`, "ok");
      const id = rejecting.id;
      setPicked((p) => { const n = new Set(p); n.delete(id); return n; });
      setRejecting(null); setNote(""); setNoteErr(false);
    } catch (e) { toast(friendly(e)); }
    setBusy(false);
  };

  const pickedRows = approvable.filter((r) => picked.has(r.id));
  const all = approvable.length > 0 && approvable.every((r) => picked.has(r.id));

  return (
    <>
      <h1>Chờ duyệt</h1>
      <p className="sub">{rows.length ? `${rows.length} tài liệu đang chờ bạn duyệt${shown.length !== rows.length ? ` · đang lọc ${shown.length}` : ""} · cũ nhất ở trên` : "Không có tài liệu nào đang chờ duyệt."}</p>
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}

      {rows.length > 3 && (
        <div className="row" style={{ marginBottom: 12 }}>
          <div className="field"><label htmlFor="rq">Tìm (không cần dấu)</label><input id="rq" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mã, tiêu đề, người tải" /></div>
          {siteOpts.length > 1 && <div className="field" style={{ flex: "0 1 180px" }}><label htmlFor="rs">Cơ sở</label>
            <select id="rs" value={site} onChange={(e) => setSite(e.target.value)}><option value="">Tất cả</option>{siteOpts.map((k) => <option key={k} value={k}>{SITES[k]}</option>)}</select></div>}
          {catOpts.length > 1 && <div className="field" style={{ flex: "0 1 200px" }}><label htmlFor="rc">Loại</label>
            <select id="rc" value={cat} onChange={(e) => setCat(e.target.value)}><option value="">Tất cả</option>{catOpts.map((k) => <option key={k} value={k}>{DOC_CATEGORIES[k]}</option>)}</select></div>}
          {uploaders.length > 1 && <div className="field" style={{ flex: "0 1 200px" }}><label htmlFor="rw">Người tải</label>
            <select id="rw" value={who} onChange={(e) => setWho(e.target.value)}><option value="">Tất cả</option>{uploaders.map((n) => <option key={n} value={n}>{n}</option>)}</select></div>}
        </div>
      )}

      {approvable.length > 1 && (
        <div className="row" style={{ marginBottom: 12 }}>
          <div className="checks"><label><input type="checkbox" checked={all} onChange={() => setPicked(all ? new Set() : new Set(approvable.map((r) => r.id)))} />Chọn tất cả{shown.length !== rows.length ? " (trong danh sách đang lọc)" : ""}</label></div>
          <button className="btn primary" disabled={!pickedRows.length || busy} onClick={() => approve(pickedRows, true)}>
            {busy ? "Đang duyệt…" : `Duyệt ${pickedRows.length || ""} mục đã chọn`}</button>
        </div>
      )}

      {rejecting && (
        <div className="card" style={{ marginBottom: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <b>Trả lại {rejecting.code} · {rejecting.title}</b>
          <div className="field" style={{ flex: "none" }}><label htmlFor="why">Lý do trả lại * (người tải lên sẽ thấy)</label>
            <textarea id="why" rows={3} value={note} onChange={(e) => { setNote(e.target.value); setNoteErr(false); }}
              style={{ border: "1px solid #D5DAD2", borderRadius: 10, padding: 10, font: "inherit" }} placeholder="vd: Thiếu trang 2, ảnh mờ…" autoFocus /></div>
          {noteErr && <div className="err">Cần ghi lý do để người tải lên biết cần sửa gì.</div>}
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button className="btn" onClick={() => { setRejecting(null); setNote(""); setNoteErr(false); }}>Hủy</button>
            <button className="btn primary" disabled={busy} onClick={reject}>Trả lại</button>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="card tableWrap" style={{ padding: 0 }}>
          <table className="table stack">
            <thead><tr><th></th><th>Mã</th><th>Tiêu đề</th><th>Cơ sở</th><th>Loại</th><th>Phiên bản</th><th>Người tải</th><th></th></tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id}>
                  <td style={{ width: 36 }}>{!selfBlocked(r) && <input type="checkbox" aria-label={`Chọn ${r.code}`} checked={picked.has(r.id)} onChange={() => toggle(r.id)} />}</td>
                  <td className="code cardHead">{r.code}</td>
                  <td>{r.title}<div className="muted">{r.subcategory} · phụ trách {r.ownerName}</div></td>
                  <td data-label="Cơ sở">{SITES[r.siteId]}</td>
                  <td data-label="Loại">{DOC_CATEGORIES[r.category]}</td>
                  <td data-label="Phiên bản">v{r.pendingVersion}{r.latestVersion ? <div className="muted">thay bản v{r.latestVersion}</div> : null}</td>
                  <td data-label="Người tải">{r.pendingByName ?? r.createdByName ?? r.createdBy}</td>
                  <td className="actions">
                    <div className="row-actions">
                      <button className="btn" onClick={() => open(r)}>Xem</button>
                      {selfBlocked(r)
                        ? <span className="muted" style={{ fontSize: 12.5, alignSelf: "center" }}>Bạn tải lên · cần người khác duyệt</span>
                        : <button className="btn primary" disabled={busy} onClick={() => approve([r])}>Duyệt</button>}
                      <button className="btn" disabled={busy} onClick={() => { setRejecting(r); setNote(""); setPicked((p) => { const n = new Set(p); n.delete(r.id); return n; }); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Trả lại</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!shown.length && <p className="muted" style={{ padding: 12, margin: 0 }}>Không có mục nào khớp bộ lọc.</p>}
        </div>
      )}
    </>
  );
}
